import React, { FunctionComponent } from "react";
import { useIntl } from "react-intl";
import CartOverviewTile from "@Components/molecules/cart/CartOverviewTile";
import useCartContext from "@Hooks/useCartContext";
import { setDataLayerTrack } from "@Utils/track";
import "./style.scss";

interface Props {
  address: {
    city?: string;
    company?: string;
    countryTag?: string;
    details?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    postcode?: string;
    street?: string;
    tvaNumber?: string;
  };
  type: "delivery" | "billing";
}

interface ContainerProps {
  onClick?: () => void;
  type: "delivery" | "billing";
}

export const CartAddressContent: FunctionComponent<Props> = ({ address, type }) => {
  return (
    <ul className="CartAddressContent">
      <li className="CartAddressContent-line CartAddressContent-name">
        {address.firstName} {address.lastName}
        {address.company && <span> - {address.company}</span>}
      </li>
      {type === "billing" && <li className="CartAddressContent-line">{address.tvaNumber}</li>}
      <li className="CartAddressContent-line">{address.street}</li>
      {address.details && <li className="CartAddressContent-line">{address.details}</li>}
      <li className="CartAddressContent-line">
        {address.postcode} {address.city}, {address.countryTag}
      </li>
      {type === "delivery" && (
        <>
          {address.phone && <li className="CartAddressContent-line">{address.phone}</li>}
          {address.email && <li className="CartAddressContent-line">{address.email}</li>}
        </>
      )}
    </ul>
  );
};

const CartAddressContentContainer: FunctionComponent<ContainerProps> = ({ onClick, type }) => {
  const { addresses } = useCartContext();
  const intl = useIntl();

  let address;
  if (type === "delivery") {
    address = {
      city: addresses.delivery.city,
      company: addresses.delivery.company,
      countryTag: addresses.delivery.countryTag2,
      details: addresses.delivery.details,
      email: addresses.delivery.email,
      firstName: addresses.delivery.firstName,
      lastName: addresses.delivery.lastName,
      phone: addresses.delivery.phone,
      postcode: addresses.delivery.postcode,
      street: addresses.delivery.street,
    };
  } else {
    address = {
      city: addresses.billing.city,
      company: addresses.billing.company,
      countryTag: addresses.billing.countryTag2,
      details: addresses.billing.details,
      firstName: addresses.billing.firstName,
      lastName: addresses.billing.lastName,
      postcode: addresses.billing.postcode,
      street: addresses.billing.street,
      tvaNumber: addresses.billing.tvaNumber,
    };
  }

  const numberRegex = new RegExp(/\d/, "g");
  const hasWarningBanner = address.street ? address.street.match(numberRegex) === null : false;
  let warningBanner:
    | {
        text: string;
        textLink: string;
      }
    | undefined;
  if (hasWarningBanner) {
    warningBanner = {
      text: intl.formatMessage({ id: "cheerz_CartAddressContent_warning_number" }),
      textLink: intl.formatMessage({ id: "cheerz_CartAddressContent_warning_link" }),
    };
    setDataLayerTrack("Payment Street Number Alert Viewed");
  }

  const handleClick = () => {
    if (onClick) {
      setDataLayerTrack("Payment Change Address Clicked");
      onClick();
    }
  };

  return (
    <CartOverviewTile
      onClick={handleClick}
      type={type === "delivery" ? "address" : type}
      warningBanner={warningBanner}
    >
      <CartAddressContent address={address} type={type} />
    </CartOverviewTile>
  );
};

export default CartAddressContentContainer;
